import {
  createUserWithEmailAndPassword,
  deleteUser,
  signInWithRedirect,
  signInWithEmailAndPassword,
  signInWithPopup,
  getAdditionalUserInfo,
  UserCredential,
  fetchSignInMethodsForEmail,
  signOut,
  unlink,
  type User,
} from "firebase/auth";
import {
  auth,
  googleProvider,
  appleProvider,
  microsoftProvider,
} from "../firebase";
import {
  isAppleVisionPro,
  isViewRNativeAuthWebView,
  isWebView,
} from "../utils/webViewUtils";
import type { LoginResponse } from "@/types/user";
import { syncAuthProviderProfileFromCredential } from "./authProviderProfile";
import { precheckEmailPasswordAccount } from "./api/auth";
import {
  createAccessRequest,
  getAccessStatus,
  type StudioPolicyConsent,
} from "./api/access";
import {
  EMAIL_ALREADY_REGISTERED_MESSAGE,
  INCORRECT_PASSWORD_MESSAGE,
  PASSWORD_CONTAINS_EMAIL_MESSAGE,
  PASSWORD_CONTAINS_SPACE_MESSAGE,
  PASSWORD_MINIMUM_LENGTH_MESSAGE,
  PASSWORD_REQUIRED_COMBINATION_MESSAGE,
  socialSignInRequiredMessage,
} from "../utils/loginAuthErrors";

const APPLE_SIGNUP_DISPLAY_NAME_KEY = "appleSignUpDisplayName";
export const PENDING_SOCIAL_REDIRECT_PROVIDER_KEY =
  "viewr_pending_social_redirect_provider";
export type PendingSocialRedirectProvider = "google" | "microsoft" | "apple";
const EMAIL_PASSWORD_CREDENTIAL_FAILURE_CODES = new Set([
  "auth/invalid-credential",
  "auth/user-not-found",
  "auth/wrong-password",
]);

export const NEW_ACCOUNT_PASSWORD_POLICY_ERROR_CODES = {
  containsSpace: "auth/password-contains-space",
  minimumLength: "auth/password-minimum-length",
  missingRequiredCombination: "auth/password-missing-required-combination",
  containsEmail: "auth/password-contains-email",
} as const;

type NewAccountPasswordPolicyErrorCode =
  (typeof NEW_ACCOUNT_PASSWORD_POLICY_ERROR_CODES)[keyof typeof NEW_ACCOUNT_PASSWORD_POLICY_ERROR_CODES];

type FirebaseAuthError = Error & { code?: string };

const getFirebaseAuthErrorCode = (error: unknown): string | null => {
  const code = (error as { code?: unknown })?.code;
  return typeof code === "string" ? code : null;
};

const createAuthError = (message: string, code: string): FirebaseAuthError => {
  const error = new Error(message) as FirebaseAuthError;
  error.code = code;
  return error;
};

export const getPendingSocialRedirectProvider =
  (): PendingSocialRedirectProvider | null => {
    if (typeof window === "undefined") return null;
    const value = sessionStorage.getItem(PENDING_SOCIAL_REDIRECT_PROVIDER_KEY);
    return value === "google" || value === "microsoft" || value === "apple"
      ? value
      : null;
  };

export const clearPendingSocialRedirectProvider = (): void => {
  if (typeof window === "undefined") return;
  sessionStorage.removeItem(PENDING_SOCIAL_REDIRECT_PROVIDER_KEY);
};

const startSocialRedirect = async (
  provider: PendingSocialRedirectProvider,
  firebaseProvider: Parameters<typeof signInWithRedirect>[1],
): Promise<void> => {
  sessionStorage.setItem(PENDING_SOCIAL_REDIRECT_PROVIDER_KEY, provider);
  try {
    await signInWithRedirect(auth, firebaseProvider);
  } catch (error) {
    clearPendingSocialRedirectProvider();
    throw error;
  }
};

/**
 * Password rules are applied only when the Login action provisions a new
 * Email/Password identity. Existing accounts retain their current password.
 */
export const getNewAccountPasswordPolicyErrorCode = (
  email: string,
  password: string,
): NewAccountPasswordPolicyErrorCode | null => {
  if (/\s/.test(password)) {
    return NEW_ACCOUNT_PASSWORD_POLICY_ERROR_CODES.containsSpace;
  }
  if (password.length < 8) {
    return NEW_ACCOUNT_PASSWORD_POLICY_ERROR_CODES.minimumLength;
  }
  if (
    !/[a-z]/.test(password) ||
    !/\d/.test(password) ||
    !/[^A-Za-z0-9\s]/.test(password)
  ) {
    return NEW_ACCOUNT_PASSWORD_POLICY_ERROR_CODES.missingRequiredCombination;
  }
  const emailId = email.trim().toLowerCase().split("@", 1)[0] || "";
  if (emailId && password.toLowerCase().includes(emailId)) {
    return NEW_ACCOUNT_PASSWORD_POLICY_ERROR_CODES.containsEmail;
  }
  return null;
};

export const validateNewAccountPassword = (
  email: string,
  password: string,
): boolean => !getNewAccountPasswordPolicyErrorCode(email, password);

const passwordPolicyErrorMessage = (
  code: NewAccountPasswordPolicyErrorCode,
): string => {
  switch (code) {
    case NEW_ACCOUNT_PASSWORD_POLICY_ERROR_CODES.containsSpace:
      return PASSWORD_CONTAINS_SPACE_MESSAGE;
    case NEW_ACCOUNT_PASSWORD_POLICY_ERROR_CODES.minimumLength:
      return PASSWORD_MINIMUM_LENGTH_MESSAGE;
    case NEW_ACCOUNT_PASSWORD_POLICY_ERROR_CODES.missingRequiredCombination:
      return PASSWORD_REQUIRED_COMBINATION_MESSAGE;
    case NEW_ACCOUNT_PASSWORD_POLICY_ERROR_CODES.containsEmail:
      return PASSWORD_CONTAINS_EMAIL_MESSAGE;
  }
};

const assertNewAccountPasswordPolicy = (
  email: string,
  password: string,
): void => {
  const code = getNewAccountPasswordPolicyErrorCode(email, password);
  if (code) {
    throw createAuthError(passwordPolicyErrorMessage(code), code);
  }
};

const getFirstNonEmptyString = (...values: Array<unknown>): string | null => {
  for (const value of values) {
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed) return trimmed;
    }
  }
  return null;
};

const extractAppleDisplayNameFromCredential = (
  result: UserCredential,
): string | null => {
  if (result.providerId !== "apple.com") return null;

  const additionalUserInfo = getAdditionalUserInfo(result);
  const profile = (additionalUserInfo?.profile || null) as {
    name?: unknown;
    given_name?: unknown;
    family_name?: unknown;
    firstName?: unknown;
    lastName?: unknown;
  } | null;

  const profileName = profile?.name;
  if (typeof profileName === "string" && profileName.trim()) {
    return profileName.trim();
  }

  if (profileName && typeof profileName === "object") {
    const nameRecord = profileName as Record<string, unknown>;
    const firstName = getFirstNonEmptyString(
      nameRecord.firstName,
      nameRecord.givenName,
      nameRecord.given_name,
    );
    const lastName = getFirstNonEmptyString(
      nameRecord.lastName,
      nameRecord.familyName,
      nameRecord.family_name,
    );
    const combinedName = [firstName, lastName].filter(Boolean).join(" ").trim();
    if (combinedName) return combinedName;
  }

  return getFirstNonEmptyString(
    profile?.firstName,
    profile?.lastName,
    profile?.given_name,
    profile?.family_name,
  );
};

const persistAppleDisplayName = (result: UserCredential): string | null => {
  const appleDisplayName = extractAppleDisplayNameFromCredential(result);
  if (appleDisplayName) {
    sessionStorage.setItem(APPLE_SIGNUP_DISPLAY_NAME_KEY, appleDisplayName);
  }
  return appleDisplayName;
};

/** getRedirectResult 등에서 account-exists-with-different-credential 시 1회 Microsoft 재시도용 */
export const ACCOUNT_EXISTS_MS_REDIRECT_RECOVERY_KEY =
  "viewr_account_exists_ms_redirect_recovery";

/**
 * Microsoft 리디렉션 직후 Firebase가 auth/account-exists-with-different-credential을 던질 때,
 * 동일 이메일에 microsoft.com이 등록되어 있으면 signOut 후 Microsoft 리디렉션을 한 번만 재시도합니다.
 * (Apple 시도 직후 리디렉션 상태가 꼬인 경우 등)
 * @returns true면 리디렉션을 이미 시작했으므로 호출부는 추가 setError 생략 가능
 */
export async function tryRecoverMicrosoftRedirectAfterAccountExistsError(
  error: unknown,
): Promise<boolean> {
  const err = error as { code?: string; customData?: { email?: string } };
  if (err?.code !== "auth/account-exists-with-different-credential") {
    return false;
  }
  try {
    await signOut(auth);
  } catch {
    // ignore
  }
  const email = err.customData?.email;
  if (!email) {
    return false;
  }
  let methods: string[] = [];
  try {
    methods = await fetchSignInMethodsForEmail(auth, email);
  } catch {
    return false;
  }
  if (!methods.includes("microsoft.com")) {
    sessionStorage.removeItem(ACCOUNT_EXISTS_MS_REDIRECT_RECOVERY_KEY);
    return false;
  }
  if (sessionStorage.getItem(ACCOUNT_EXISTS_MS_REDIRECT_RECOVERY_KEY) === "1") {
    sessionStorage.removeItem(ACCOUNT_EXISTS_MS_REDIRECT_RECOVERY_KEY);
    return false;
  }
  sessionStorage.setItem(ACCOUNT_EXISTS_MS_REDIRECT_RECOVERY_KEY, "1");
  await startSocialRedirect("microsoft", microsoftProvider);
  return true;
}

export function normalizeSignInForUnlink(
  raw: string | null | undefined,
): string {
  if (!raw) return "unknown";
  const p = String(raw).toLowerCase().trim();
  if (p === "password") return "email";
  if (p.includes("microsoft")) return "microsoft";
  if (p.includes("apple")) return "apple";
  if (p.includes("google")) return "google";
  return "unknown";
}

/** Firebase ID token `firebase.sign_in_provider` (anonymous is not Studio-supported). */
export async function resolveFirebaseSignInProvider(
  user: User,
): Promise<string> {
  const idTokenResult = await user.getIdTokenResult(false);
  const claims = idTokenResult.claims as {
    firebase?: { sign_in_provider?: string };
  };
  const fromToken =
    (idTokenResult as { signInProvider?: string | null }).signInProvider ??
    claims?.firebase?.sign_in_provider;
  return fromToken ?? user.providerData[0]?.providerId ?? "unknown";
}

export type StudioLoginMethod =
  "google" | "apple" | "microsoft" | "email" | "guest";

export function studioLoginMethodFromSignInProvider(
  signInProvider: string,
): StudioLoginMethod {
  const norm = normalizeSignInForUnlink(signInProvider);
  if (
    norm === "microsoft" ||
    norm === "apple" ||
    norm === "google" ||
    norm === "email"
  ) {
    return norm;
  }
  return "guest";
}

const NORM_TO_FIREBASE_PROVIDER_ID: Record<string, string> = {
  microsoft: "microsoft.com",
  apple: "apple.com",
  google: "google.com",
  email: "password",
};

/** apiCall이 던진 Error 메시지에서 409 JSON 본문 파싱 */
export function parseSocialConflict409Payload(err: Error): {
  message?: string;
  stored_login_method?: string;
} | null {
  const m = err.message.match(/HTTP error! status: 409 - (.+)$/);
  if (!m?.[1]) return null;
  try {
    return JSON.parse(m[1]) as {
      message?: string;
      stored_login_method?: string;
    };
  } catch {
    return null;
  }
}

/**
 * DB 정본 소셜(storedNorm: microsoft|apple|…)과 다른 세션 제공자가 Firebase에 붙어 있으면 unlink.
 * 동일 이메일 계정 연결로 Apple이 잠깐 붙은 뒤 Console에 Apple로 보이는 문제 완화.
 */
export async function unlinkSessionProviderIfNotCanonical(
  user: User,
  storedNorm: string | undefined,
): Promise<boolean> {
  if (!storedNorm || storedNorm === "unknown") return false;
  const idTokenResult = await user.getIdTokenResult(true);
  const claims = idTokenResult.claims as {
    firebase?: { sign_in_provider?: string };
  };
  const signInRaw =
    (idTokenResult as { signInProvider?: string | null }).signInProvider ??
    claims?.firebase?.sign_in_provider;
  const tokenNorm = normalizeSignInForUnlink(signInRaw ?? undefined);
  if (tokenNorm === "unknown" || tokenNorm === storedNorm) return false;
  const pid = NORM_TO_FIREBASE_PROVIDER_ID[tokenNorm];
  if (!pid) return false;
  if (!user.providerData.some((p) => p.providerId === pid)) return false;
  try {
    await unlink(user, pid);
    return true;
  } catch (e) {
    console.warn("unlink non-canonical provider failed:", e);
    return false;
  }
}

/**
 * 이메일/비밀번호 로그인
 */
const getEmailPasswordLoginResponse = async (
  user: User,
): Promise<LoginResponse> => {
  const token = await user.getIdToken();

  return {
    success: true,
    user: {
      uid: user.uid,
      email: user.email || null,
      displayName: user.displayName || null,
      photoURL: user.photoURL || null,
      token,
      providerData: user.providerData.map((p) => ({
        providerId: p.providerId,
        uid: p.uid,
        displayName: p.displayName || null,
        email: p.email || null,
        photoURL: p.photoURL || null,
      })),
    },
    token,
  };
};

export const loginAPI = async (
  email: string,
  password: string,
): Promise<LoginResponse> => {
  const normalizedEmail = email.toLowerCase();

  let accountType: Awaited<
    ReturnType<typeof precheckEmailPasswordAccount>
  >["accountType"];
  try {
    ({ accountType } = await precheckEmailPasswordAccount(normalizedEmail));
  } catch {
    throw createAuthError(
      "Unable to check the email account.",
      "auth/email-password-precheck-unavailable",
    );
  }

  if (accountType === "unregistered") {
    throw createAuthError(
      "Request access before creating a password.",
      "auth/access-approval-required",
    );
  }

  const socialProviderId =
    accountType === "google"
      ? "google.com"
      : accountType === "microsoft"
        ? "microsoft.com"
        : accountType === "apple"
          ? "apple.com"
          : null;
  if (socialProviderId) {
    throw createAuthError(
      socialSignInRequiredMessage(socialProviderId),
      "auth/social-sign-in-required",
    );
  }
  if (accountType === "registered") {
    throw createAuthError(
      EMAIL_ALREADY_REGISTERED_MESSAGE,
      "auth/email-already-in-use",
    );
  }

  try {
    const result = await signInWithEmailAndPassword(
      auth,
      normalizedEmail,
      password,
    );
    return getEmailPasswordLoginResponse(result.user);
  } catch (signInError) {
    if (
      EMAIL_PASSWORD_CREDENTIAL_FAILURE_CODES.has(
        getFirebaseAuthErrorCode(signInError) || "",
      )
    ) {
      throw createAuthError(INCORRECT_PASSWORD_MESSAGE, "auth/wrong-password");
    }
    throw signInError;
  }
};

/** Create the Firebase Password identity only for an already-approved request. */
export const createApprovedEmailPasswordAccount = async (
  email: string,
  password: string,
  consent: StudioPolicyConsent,
): Promise<LoginResponse> => {
  const normalizedEmail = email.toLowerCase();
  assertNewAccountPasswordPolicy(normalizedEmail, password);

  const precheck = await precheckEmailPasswordAccount(normalizedEmail);
  if (
    precheck.nextAction !== "access_approved" ||
    precheck.assignedProvider !== "password"
  ) {
    throw createAuthError(
      "This email is not ready to create an account.",
      "auth/access-approval-required",
    );
  }
  if (
    precheck.policy.termsVersion !== consent.termsVersion ||
    precheck.policy.privacyVersion !== consent.privacyVersion
  ) {
    throw createAuthError(
      "The Studio policy changed. Review and accept the current policy.",
      "auth/policy-version-outdated",
    );
  }

  let result: UserCredential;
  try {
    result = await createUserWithEmailAndPassword(
      auth,
      normalizedEmail,
      password,
    );
  } catch (createAccountError) {
    if (
      getFirebaseAuthErrorCode(createAccountError) ===
      "auth/email-already-in-use"
    ) {
      throw createAuthError(
        EMAIL_ALREADY_REGISTERED_MESSAGE,
        "auth/email-already-in-use",
      );
    }
    throw createAccountError;
  }

  try {
    const access = await createAccessRequest(undefined, consent);
    if (access.permissionScope !== "full") {
      throw createAuthError(
        "Approved Studio access was not activated.",
        "auth/access-activation-incomplete",
      );
    }
    return await getEmailPasswordLoginResponse(result.user);
  } catch (claimError) {
    // The activation may have committed before the response was lost. Keep
    // that identity only when the server confirms its active membership.
    try {
      const access = await getAccessStatus();
      if (access.permissionScope === "full") {
        return await getEmailPasswordLoginResponse(result.user);
      }
    } catch {
      // Fall through to deleting the unactivated Firebase identity.
    }
    await deleteUser(result.user).catch(() => undefined);
    throw claimError;
  }
};

/**
 * Vision Pro 네이티브 WebView는 현재 창에서 redirect를 유지한다.
 * iframe/Figma 같은 embedded 웹 컨텍스트만 popup을 사용한다.
 */
function shouldUsePopupAuth(): boolean {
  return isEmbeddedAuthContext() && !isViewRNativeAuthWebView();
}

export const loginWithGoogle = async (): Promise<void> => {
  try {
    if (shouldUsePopupAuth()) {
      const result = await signInWithPopup(auth, googleProvider);
      await syncAuthProviderProfileFromCredential(result);
      return;
    }
    await startSocialRedirect("google", googleProvider);
    // getRedirectResult는 AuthContext에서 처리
  } catch (error) {
    console.error("❌ Google 로그인 에러:", error);
    throw error;
  }
};

/** Figma Plugin iframe 등 embedded context 감지 (popup 창은 제외) */
export function isEmbeddedAuthContext(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get("popup") === "1") return false;
    if (params.get("embedded") === "1" || params.get("figma_plugin") === "1") {
      return true;
    }
    if (window.self !== window.top) return true;
  } catch {
    return true;
  }
  return false;
}

/** Figma Plugin 로그인 팝업 창 */
export function isFigmaPluginPopupContext(): boolean {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  if (params.get("popup") === "1") return true;
  try {
    return Boolean(window.opener && window.self === window.top);
  } catch {
    return false;
  }
}

export const loginWithGooglePopup = async (): Promise<void> => {
  const result = await signInWithPopup(auth, googleProvider);
  await syncAuthProviderProfileFromCredential(result);
};

export const loginWithMicrosoftPopup = async (): Promise<void> => {
  const result = await signInWithPopup(auth, microsoftProvider);
  await syncAuthProviderProfileFromCredential(result);
};

export const loginWithApplePopup = async (): Promise<UserCredential> => {
  const result = await signInWithPopup(auth, appleProvider);
  persistAppleDisplayName(result);
  await syncAuthProviderProfileFromCredential(result);
  return result;
};

/**
 * Apple 로그인 함수 (visionOS WebView에서는 같은 WebView 내에서 처리)
 */
export const loginWithApple = async (): Promise<LoginResponse | void> => {
  try {
    const isVisionPro = isAppleVisionPro();
    const inWebView = isWebView();

    // visionOS WebView / Figma Plugin iframe 등 embedded context
    if (isVisionPro || inWebView || isEmbeddedAuthContext()) {
      try {
        const result = await signInWithPopup(auth, appleProvider);
        persistAppleDisplayName(result);
        await syncAuthProviderProfileFromCredential(result);
        const user = result.user;
        const token = await user.getIdToken();
        const isNativeAvpAuthWebView = isViewRNativeAuthWebView();

        // Native AVP handoff is gated by AuthContext after Main API approval.
        // Generic embedded clients keep the legacy URL/message transport.
        const currentUrl = new URL(window.location.href);
        if (!isNativeAvpAuthWebView) {
          currentUrl.searchParams.set("token", token);
          currentUrl.searchParams.set("uid", user.uid);
          currentUrl.searchParams.set("email", user.email || "");
          currentUrl.searchParams.set("provider", "apple");
          currentUrl.searchParams.set("auth_status", "success");
        }

        // WebView에 토큰 전달
        const windowWithWebKit = window as unknown as {
          webkit?: {
            messageHandlers?: {
              authHandler?: {
                postMessage: (msg: unknown) => void;
              };
            };
          };
        };

        if (
          !isNativeAvpAuthWebView &&
          windowWithWebKit.webkit?.messageHandlers?.authHandler
        ) {
          const message = {
            type: "AUTH_SUCCESS",
            token: token,
            userInfo: {
              uid: user.uid,
              email: user.email,
              provider: "apple",
              isGuest: false,
            },
            timestamp: new Date().toISOString(),
            platform: "visionos",
            source: "apple_popup",
          };

          windowWithWebKit.webkit.messageHandlers.authHandler.postMessage(
            message,
          );
        }

        // URL 업데이트 (히스토리 추가 없이)
        if (!isNativeAvpAuthWebView) {
          window.history.replaceState({}, "", currentUrl.toString());
        }

        return {
          success: true,
          user: {
            uid: user.uid,
            email: user.email || null,
            displayName: user.displayName || null,
            photoURL: user.photoURL || null,
            token: token,
            providerData: user.providerData.map((p) => ({
              providerId: p.providerId,
              uid: p.uid,
              displayName: p.displayName || null,
              email: p.email || null,
              photoURL: p.photoURL || null,
            })),
          },
          token: token,
        };
      } catch (popupError) {
        console.error("❌ Apple 팝업 로그인 에러:", popupError);
        if (isEmbeddedAuthContext()) {
          throw popupError;
        }
        // 방법 2: signInWithRedirect 사용 (같은 WebView 내에서 처리되도록)
        await startSocialRedirect("apple", appleProvider);

        // 리디렉션 후 결과는 AuthContext에서 getRedirectResult로 처리됨
        // 여기서는 리디렉션만 시작
      }
    } else {
      // 일반 브라우저: 리디렉션 방식
      await startSocialRedirect("apple", appleProvider);
      // getRedirectResult는 AuthContext에서 처리
      return;
    }
  } catch (error) {
    console.error("❌ Apple 로그인 에러:", error);
    throw error;
  }
};

/**
 * Microsoft 로그인 함수 (리디렉션)
 */
export const loginWithMicrosoft = async (): Promise<void> => {
  try {
    if (shouldUsePopupAuth()) {
      const result = await signInWithPopup(auth, microsoftProvider);
      await syncAuthProviderProfileFromCredential(result);
      return;
    }
    await startSocialRedirect("microsoft", microsoftProvider);
    // getRedirectResult는 AuthContext에서 처리
  } catch (error) {
    console.error("❌ Microsoft 로그인 에러:", error);
    throw error;
  }
};

/**
 * Google 리디렉션 로그인 함수 (백업용)
 */
export const loginWithGoogleRedirect = async (): Promise<void> => {
  try {
    await startSocialRedirect("google", googleProvider);
    // getRedirectResult는 AuthContext에서만 호출
  } catch (error) {
    console.error("❌ Google 리디렉션 로그인 에러:", error);
    throw error;
  }
};
